/*
 * Creator pricing — shows what a price becomes with the shopper's discount code.
 *
 * A Shopify discount is a rule about an order, not a property of a product, so
 * nothing on a product or collection page knows what a code is worth. Only the
 * cart does. This asks the cart: it builds a throwaway cart through the
 * Storefront API with the applied code, and reads back what Shopify says the
 * total is. Same engine as checkout, so eligibility, minimums and product scope
 * are Shopify's answer, never our arithmetic.
 *
 * The Storefront API needs no access token here — the theme is same-origin with
 * the shop domain.
 *
 * On a product page the exact variant is priced, so the number shown is the
 * truth for that item. Elsewhere the rate discovered at the start of the session
 * is reused, and only if two different products agreed on it; if they disagree,
 * the discount is uneven (product-scoped or fixed-amount) and only product pages
 * are annotated. Anything unexpected — a failed request, an unparseable price, a
 * code that doesn't apply — renders nothing. A missing line is invisible; a
 * wrong price is a broken promise.
 */
(function () {
  'use strict';

  var API = '/api/2026-04/graphql.json';
  var RATE_KEY = 'mz-creator-rate';
  var EPSILON = 0.0005;

  function cookie(name) {
    var match = ('; ' + document.cookie).match(new RegExp('; ' + name + '=([^;]*)'));
    if (!match) return '';
    try {
      return decodeURIComponent(match[1]);
    } catch (e) {
      return match[1];
    }
  }

  function session(key, value) {
    try {
      if (value === undefined) return window.sessionStorage.getItem(key);
      window.sessionStorage.setItem(key, value);
    } catch (e) {
      /* private mode: recompute each page instead of caching */
    }
    return null;
  }

  // Dots are common in handles (priya.kaur), and a Collabs code is usually the
  // creator's handle, so the charset has to be wider than letters and dashes.
  var code = cookie('discount_code');
  if (!code || !/^[A-Za-z0-9._-]{2,64}$/.test(code)) return;

  function graphql(query, variables) {
    return fetch(API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: query, variables: variables || {} }),
    })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .then(function (json) {
        return json && !json.errors ? json.data : null;
      })
      .catch(function () {
        return null;
      });
  }

  // What Shopify says this exact variant costs with the code on it.
  var PROBE = [
    'mutation Probe($code: String!, $id: ID!) {',
    '  cartCreate(input: {discountCodes: [$code], lines: [{merchandiseId: $id, quantity: 1}]}) {',
    '    cart {',
    '      discountCodes { applicable }',
    '      cost { subtotalAmount { amount } totalAmount { amount } }',
    '    }',
    '  }',
    '}',
  ].join('\n');

  function probe(variantId) {
    return graphql(PROBE, { code: code, id: variantId }).then(function (data) {
      var cart = data && data.cartCreate && data.cartCreate.cart;
      if (!cart) return null;
      var codes = cart.discountCodes || [];
      if (!codes.length || !codes[0].applicable) return { rate: 0, applicable: false };

      var subtotal = parseFloat(cart.cost.subtotalAmount.amount);
      var total = parseFloat(cart.cost.totalAmount.amount);
      if (!(subtotal > 0) || !(total >= 0) || total > subtotal) return null;

      return { rate: (subtotal - total) / subtotal, applicable: true };
    });
  }

  var SAMPLE = [
    'query Sample {',
    '  products(first: 4) {',
    '    nodes { variants(first: 1) { nodes { id } } }',
    '  }',
    '}',
  ].join('\n');

  // Two products, because one can't tell a flat percentage from a discount that
  // only covers part of the catalogue.
  function discoverRate() {
    var cached = session(RATE_KEY);
    if (cached) {
      try {
        var parsed = JSON.parse(cached);
        if (parsed && parsed.code === code) return Promise.resolve(parsed);
      } catch (e) {
        /* fall through and rediscover */
      }
    }

    return graphql(SAMPLE).then(function (data) {
      var nodes = (data && data.products && data.products.nodes) || [];
      var ids = nodes
        .map(function (node) {
          return node.variants.nodes[0] && node.variants.nodes[0].id;
        })
        .filter(Boolean);
      if (ids.length < 2) return null;

      return Promise.all(ids.slice(0, 4).map(probe)).then(function (all) {
        // Some products can't be priced — sold out, or excluded from the
        // discount. Judge uniformity on the ones that could be.
        var results = all.filter(function (result) {
          return result && result.applicable;
        });
        if (results.length < 2) return null;

        var rate = results[0].rate;
        var uniform = Math.abs(results[0].rate - results[1].rate) < EPSILON && rate > 0;
        var discovered = { code: code, rate: rate, uniform: uniform };
        session(RATE_KEY, JSON.stringify(discovered));
        return discovered;
      });
    });
  }

  // Rewrites the number inside the theme's own price string, so currency symbol,
  // separators and placement stay exactly as the theme rendered them.
  var MONEY = /(\d[\d.,]*)/;

  function parseMoney(text) {
    var found = text.match(MONEY);
    if (!found) return null;
    var raw = found[1];
    var normalised = raw.indexOf('.') !== -1 ? raw.replace(/,/g, '') : raw.replace(/,/g, '');
    var value = parseFloat(normalised);
    return isFinite(value) ? { value: value, raw: raw } : null;
  }

  function formatLike(original, value) {
    var money = parseMoney(original);
    if (!money) return null;
    var decimals = money.raw.indexOf('.') === -1 ? 0 : money.raw.split('.')[1].length;
    var formatted = value.toLocaleString('en-US', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    return original.replace(money.raw, formatted);
  }

  // The cart is the one place Shopify has already done this arithmetic: it
  // renders final_line_price with the discount taken off. Annotating there would
  // discount an already-discounted figure and show a price below reality. Today
  // those containers hold no .price-item so they're skipped by accident — this
  // makes it deliberate, and keeps it true if the cart markup ever changes.
  var CART_SURFACES = '.cart-item, cart-drawer, #main-cart-items, #main-cart-footer, .cart__footer, .totals, .cart-notification';

  function inCart(element) {
    return typeof element.closest === 'function' && !!element.closest(CART_SURFACES);
  }

  function priceNodeIn(container) {
    var onSale = container.classList.contains('price--on-sale');
    return (
      container.querySelector(onSale ? '.price__sale .price-item--sale' : '.price__regular .price-item--regular') ||
      container.querySelector('.price-item--regular')
    );
  }

  // Shopify's own subtotal-vs-total is the source of the percentage; nothing is
  // inferred from the rendered prices. Floored rather than rounded, so a 7.5%
  // discount reads as 7% — understating is honest, overstating is a promise the
  // checkout won't keep.
  function percentOff(rate) {
    var percent = Math.floor(rate * 100 + 0.0001);
    return percent >= 1 && percent <= 99 ? percent : 0;
  }

  // The price the shopper will actually pay goes first; the original drops below
  // it, struck, with the saving beside it.
  function annotate(container, rate) {
    if (container.dataset.creatorPriced || inCart(container)) return;

    var priceNode = priceNodeIn(container);
    if (!priceNode) return;

    var money = parseMoney(priceNode.textContent);
    if (!money || !(money.value > 0)) return;

    var discounted = formatLike(priceNode.textContent.trim(), money.value * (1 - rate));
    if (!discounted) return;

    var amount = document.createElement('span');
    amount.className = 'creator-price__amount';

    var value = document.createElement('span');
    value.textContent = discounted;
    amount.appendChild(value);

    container.insertBefore(amount, container.firstChild);
    container.classList.add('creator-priced');

    // Sits on the second line, next to the struck original: it explains that
    // price, not the one being paid. The code doubles as the creator's handle in
    // a Collabs setup, so it's credited verbatim — never re-cased, re-spaced or
    // second-guessed.
    var percent = percentOff(rate);
    if (percent) {
      var savings = document.createElement('span');
      savings.className = 'creator-price__savings';
      savings.textContent = '(' + percent + '% off from @' + code + ')';
      container.appendChild(savings);
    }
    container.dataset.creatorPriced = 'true';
  }

  // The community page says "X sent you here"; this adds the figure underneath,
  // once Shopify has confirmed it. Only called for a rate that holds across the
  // store — the sentence claims exactly that.
  function announceStorewideRate(rate) {
    var note = document.querySelector('[data-community-discount-note]');
    if (!note) return;

    var percent = percentOff(rate);
    var template = note.getAttribute('data-community-discount-note');
    if (!percent || !template) return;

    note.textContent = template.replace('[percent]', percent).replace('{percent}', percent);
    note.hidden = false;
  }

  function annotateAll(rate) {
    var containers = document.querySelectorAll('.price');
    for (var i = 0; i < containers.length; i++) annotate(containers[i], rate);
  }

  // Dawn rewrites the price block when a variant changes or a section re-renders,
  // which drops our line. Watch for that and put it back.
  function keepAnnotated(rate, probedVariantId) {
    var currentRate = rate;
    var probedFor = probedVariantId || null;

    annotateAll(currentRate);
    if (typeof MutationObserver !== 'function' || !document.body) return;

    var pending = null;
    new MutationObserver(function () {
      if (pending) return;
      pending = setTimeout(function () {
        pending = null;

        // Switching variant can change the price, and for a fixed-amount
        // discount that changes the rate with it — so the new variant is
        // re-priced rather than reusing the old one's percentage.
        var variantId = currentVariantId();
        if (probedFor && variantId && variantId !== probedFor) {
          probedFor = variantId;
          probe(variantId).then(function (result) {
            if (result && result.applicable && result.rate > 0) {
              currentRate = result.rate;
              annotateAll(currentRate);
            }
          });
          return;
        }

        annotateAll(currentRate);
      }, 100);
    }).observe(document.body, { childList: true, subtree: true });
  }

  function currentVariantId() {
    var input = document.querySelector('form[action*="/cart/add"] input[name="id"]');
    if (!input || !input.value) return null;
    return 'gid://shopify/ProductVariant/' + input.value;
  }

  function start() {
    var variantId = currentVariantId();

    // A product page can price its own variant exactly, so it never relies on
    // the session rate.
    if (variantId) {
      probe(variantId).then(function (result) {
        if (result && result.applicable && result.rate > 0) {
          announceStorewideRate(result.rate);
          keepAnnotated(result.rate, variantId);
        }
      });
      return;
    }

    discoverRate().then(function (discovered) {
      if (discovered && discovered.uniform) {
        announceStorewideRate(discovered.rate);
        keepAnnotated(discovered.rate);
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
